import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell, faSignOutAlt, faHome, faQuestionCircle,
  faSearch, faTimes, faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import logo from "../img/logo.png";
import { useAuth } from "../context/AuthContext";
import AvatarUsuario from "./Colegiado/AvatarUsuario";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

/* ===== Helpers ===== */
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const authFetch = async (url, token, opts = {}) => {
  const res = await fetch(url, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    cache: "no-store",
  });
  return res;
};
function normalizeBasic(s = "") {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim();
}
function buildTerms(q) { return normalizeBasic(q).split(" ").filter(Boolean); }
function pageMatches(textNorm, terms) { return terms.every((t) => textNorm.includes(t)); }
function makeSnippet(text, hitIndex, termLen, ctx = 90) {
  const start = Math.max(0, hitIndex - ctx);
  const end = Math.min(text.length, hitIndex + termLen + ctx);
  const raw = text.slice(start, end);
  return (start > 0 ? "… " : "") + raw + (end < text.length ? " …" : "");
}

const Navbar = ({ isMobile }) => {
  const { logout, usuario } = useAuth();
  const token = localStorage.getItem("token");
  const [avisosNoLeidos, setAvisosNoLeidos] = useState(0);

  // búsqueda
  const [query, setQuery] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [fulltext, setFulltext] = useState(null);
  const [filtered, setFiltered] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const navigate = useNavigate();

  const isAdmin = (usuario?.rol || "").toUpperCase() === "ADMIN";
  const avisosPath = isAdmin ? "/admin/avisos" : "/avisos";
  const helpPath = isAdmin ? "/manual/admin" : "/manual/colegiado";

  const cargarAvisos = async () => {
    try {
      if (!usuario?.codigo || !token) return;
      const res = await axios.get(
        `${API_BASE}/api/avisos/usuario/${usuario.codigo}/countNoLeidos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvisosNoLeidos(Number(res.data) || 0);
    } catch (err) {
      console.error("Error cargando avisos:", err);
    }
  };

  useEffect(() => {
    cargarAvisos();
    const onRefresh = () => cargarAvisos();
    const onFocus = () => cargarAvisos();
    const onVisibility = () => { if (document.visibilityState === "visible") cargarAvisos(); };
    window.addEventListener("avisos:refresh", onRefresh);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const intervalo = setInterval(cargarAvisos, 60000);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener("avisos:refresh", onRefresh);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [usuario?.codigo, token]);

  // Carga índice full-text con Authorization
  useEffect(() => {
    let alive = true;
    setLoadErr(null);
    setFulltext(null);
    (async () => {
      try {
        const role = isAdmin ? "ADMIN" : "COLEGIADO";
        const url = `${API_BASE}/api/manuales/fulltext?role=${encodeURIComponent(role)}`;
        const resp = await authFetch(url, token);
        if (resp.status === 401) {
          setLoadErr("HTTP 401");
          return; // evita intentar parsear
        }
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const pagesInput = Array.isArray(data?.pages) ? data.pages : [];
        const prepared = {
          pages: pagesInput.map((p) => {
            const txt = (p?.text || "").replace(/\r/g, " ").replace(/\n/g, " ");
            return { page: Number(p?.page) || 1, text: txt, _norm: normalizeBasic(txt) };
          }),
        };
        if (alive) setFulltext(prepared);
      } catch (e) {
        console.error("[Navbar] índice full-text:", e);
        if (alive) { setLoadErr(String(e?.message || e)); setFulltext({ pages: [] }); }
      }
    })();
    return () => { alive = false; };
  }, [isAdmin, token]);

  // Filtrado
  useEffect(() => {
    const q = (query || "").trim();
    if (!q || !fulltext?.pages?.length) { setFiltered([]); return; }
    const terms = buildTerms(q);
    const out = [];
    for (const p of fulltext.pages) {
      if (!p._norm) continue;
      if (pageMatches(p._norm, terms)) {
        const first = terms[0];
        const hitIndex = p._norm.indexOf(first);
        const snippet = makeSnippet(p.text, Math.max(0, hitIndex), first.length, 90);
        out.push({ page: p.page, title: `Coincidencia en página ${p.page}`, snippet });
        if (out.length >= 20) break;
      }
    }
    setFiltered(out);
  }, [query, fulltext]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const openManual = () => navigate(helpPath);
  const openManualAt = (page) => navigate(`${helpPath}?page=${Number(page) || 1}`);

  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    if (filtered.length > 0) openManualAt(filtered[0].page);
    else navigate(helpPath);
    setShowPopup(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 w-full z-50 shadow-md bg-gradient-to-b from-[#263858] via-[#3c4c6a] to-[#263858]">
        <div className="max-w-full px-4 py-2 flex items-center text-white">
          <div className="flex items-center"><img src={logo} alt="EnferNET" className="h-10" /></div>

          {isMobile ? (
            <div className="flex flex-1 justify-end space-x-3 items-center">
              <button className="p-2 rounded hover:bg-white/20" title="Buscar"
                onClick={() => { setShowPopup(true); setTimeout(() => document.getElementById("manual-search-input")?.focus(), 0); }}>
                <FontAwesomeIcon icon={faSearch} size="lg" />
              </button>

              <div className="p-1 mr-2"><AvatarUsuario usuario={usuario} size={32} /></div>

              <Link to="/dashboard" className="text-white p-2 hover:bg-white/20 rounded" title="Inicio">
                <FontAwesomeIcon icon={faHome} size="lg" />
              </Link>

              <button className="text-white p-2 hover:bg-white/20 rounded" title="Ayuda" onClick={openManual}>
                <FontAwesomeIcon icon={faQuestionCircle} size="lg" />
              </button>

              <button className="relative p-2 hover:bg-white/20 rounded" title="Notificaciones" onClick={() => navigate(avisosPath)}>
                <FontAwesomeIcon icon={faBell} size="lg" />
                {avisosNoLeidos > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{avisosNoLeidos}</span>
                )}
              </button>

              <button onClick={handleLogout} className="text-white p-2 hover:bg-red-600 rounded" title="Salir">
                <FontAwesomeIcon icon={faSignOutAlt} size="lg" />
              </button>
            </div>
          ) : (
            <div className="flex flex-1 items-center divide-x divide-gray-300 justify-end">
              <div className="px-4 mr-4"><AvatarUsuario usuario={usuario} size={40} /></div>

              <Link to="/dashboard" className="px-4 py-2 text-white hover:bg-white/20">Inicio</Link>

              <button onClick={openManual} className="px-4 py-2 text-white hover:bg-white/20">Ayuda</button>

              <div className="px-4">
                <form onSubmit={onSubmitSearch} className="flex items-center gap-2 bg-white rounded px-2 py-1"
                  onClick={() => { setShowPopup(true); setTimeout(() => document.getElementById("manual-search-input")?.focus(), 0); }}>
                  <FontAwesomeIcon icon={faSearch} className="text-gray-600" />
                  <input
                    type="text" placeholder="Buscar" className="outline-none text-black w-40"
                    value={query} onChange={(e) => setQuery(e.target.value)} onFocus={() => setShowPopup(true)} id="manual-search-input"
                  />
                </form>
              </div>

              <button className="px-4 relative" onClick={() => navigate(avisosPath)} title="Notificaciones">
                <FontAwesomeIcon icon={faBell} className="text-white" />
                {avisosNoLeidos > 0 && (
                  <span className="absolute -top-3 left-2 bg-red-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{avisosNoLeidos}</span>
                )}
              </button>

              <button onClick={handleLogout} className="px-4 py-2 flex items-center text-white hover:bg-red-600">
                <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                Salir
              </button>
            </div>
          )}
        </div>
        <div className="h-[1px] bg-gray-500 w-full" />
      </nav>

      {showPopup && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-24" onClick={() => setShowPopup(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faSearch} />
                <input
                  id="manual-search-input" className="w-72 md:w-96 outline-none"
                  placeholder={`Buscar en ${isAdmin ? "Manual Admin" : "Manual Colegiado"}…`}
                  value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") onSubmitSearch(e); }} autoFocus
                />
              </div>
              <div className="flex items-center gap-2">
                <button className="text-sm px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  onClick={() => navigate(helpPath)} title="Abrir manual completo">
                  Abrir manual <FontAwesomeIcon icon={faExternalLinkAlt} className="ml-1" />
                </button>
                <button className="p-2 rounded hover:bg-gray-100" onClick={() => setShowPopup(false)} title="Cerrar">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-auto p-3">
              {loadErr && <div className="text-sm text-red-600 mb-2">No se pudo cargar el índice ({loadErr}).</div>}
              {query && filtered.length === 0 && !loadErr && (
                <div className="text-sm text-gray-600 px-1 py-2">Sin resultados para “{query}”.</div>
              )}
              {filtered.map((it, idx) => (
                <div key={`${it.page}-${idx}`} className="p-3 rounded hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  onClick={() => { openManualAt(it.page); setShowPopup(false); }}>
                  <div className="text-sm text-gray-500 mb-1">Página {it.page}</div>
                  <div className="font-semibold">{it.title}</div>
                  {it.snippet && <div className="text-sm text-gray-700 mt-1">{it.snippet}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
